import { useEffect } from 'react';
import { useGameStore } from '../store/gameStore';
import { signalRService } from '@/lib/signalr/hubConnection';
import { toast } from 'sonner';
import type { Player, Question } from '../types/game.types';

export const useGameSignalR = () => {
  const {
    addPlayer,
    removePlayer,
    updatePlayer,
    setCurrentQuestion,
    setTimeRemaining,
    setGameStatus,
    revealCorrectAnswer,
  } = useGameStore();

  useEffect(() => {
    const connectAndListen = async () => {
      try {
        const hubUrl = import.meta.env.VITE_SIGNALR_HUB_URL || `${import.meta.env.VITE_API_URL}/gamehub`;
        await signalRService.connect(hubUrl);

        // Listen to server events
        signalRService.on('PlayerJoined', (player: Player) => {
          addPlayer(player);
          toast.success(`${player.username} joined the game`);
        });

        signalRService.on('PlayerLeft', (userId: string) => {
          removePlayer(userId);
          toast.info('A player left the game');
        });

        signalRService.on('PlayerReady', (userId: string) => {
          updatePlayer(userId, { isReady: true });
        });

        signalRService.on('GameStarted', ({ totalQuestions: _totalQuestions }: { totalQuestions: number }) => {
          void _totalQuestions;
          setGameStatus('Active');
          toast.success('Game started!');
        });

        signalRService.on('QuestionPresented', ({ question, index, startedAt }: { question: Question; index: number; startedAt: string }) => {
          setCurrentQuestion(question, index);
          
          // Start client-side countdown
          const serverTime = new Date(startedAt).getTime();
          const clientTime = Date.now();
          const latency = clientTime - serverTime;
          const adjustedTimeLimit = Math.max(0, question.timeLimit * 1000 - latency);
          
          let remaining = adjustedTimeLimit;
          const interval = setInterval(() => {
            remaining -= 100;
            if (remaining <= 0) {
              clearInterval(interval);
              setTimeRemaining(0);
            } else {
              setTimeRemaining(Math.ceil(remaining / 1000));
            }
          }, 100);
        });

        signalRService.on('TimeExpired', ({ correctAnswerId, scores }: { correctAnswerId: string; scores: Array<{ userId: string; score: number }> }) => {
          revealCorrectAnswer(correctAnswerId);
          
          // Update player scores
          scores.forEach(({ userId, score }) => {
            updatePlayer(userId, { score });
          });
        });

        signalRService.on('GameEnded', ({ finalScores: _finalScores, winnerId: _winnerId }: { finalScores: Array<{ userId: string; score: number }>; winnerId: string }) => {
          void _finalScores;
          void _winnerId;
          setGameStatus('Ended');
          toast.success('Game ended!');
        });

        signalRService.on('Error', ({ message }: { message: string }) => {
          toast.error(message);
        });

      } catch (error) {
        console.error('SignalR connection failed', error);
        toast.error('Failed to connect to game server');
      }
    };

    connectAndListen();

    return () => {
      signalRService.disconnect();
    };
  }, []);

  // Client methods to invoke server actions
  const joinRoom = async (roomCode: string) => {
    try {
      await signalRService.invoke('JoinRoom', roomCode);
    } catch (error) {
      toast.error('Failed to join room');
      throw error;
    }
  };

  const markReady = async () => {
    try {
      await signalRService.invoke('MarkReady');
    } catch (error) {
      toast.error('Failed to mark ready');
    }
  };

  const startGame = async () => {
    try {
      await signalRService.invoke('StartGame');
    } catch (error) {
      toast.error('Failed to start game');
    }
  };

  const submitAnswer = async (answerId: string) => {
    try {
      await signalRService.invoke('SubmitAnswer', answerId);
    } catch (error) {
      toast.error('Failed to submit answer');
    }
  };

  const useLifeline = async (lifelineType: string) => {
    try {
      await signalRService.invoke('UseLifeline', lifelineType);
    } catch (error) {
      toast.error('Failed to use lifeline');
    }
  };

  return {
    joinRoom,
    markReady,
    startGame,
    submitAnswer,
    useLifeline,
  };
};
